import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Frozen extract from 03_categorical_descriptive_statistics.parquet and
// 04_numeric_descriptive_statistics.parquet in the supplied 2020–2024 report.
const ACCIDENT_TYPES = [
  { label: "Столкновение", accidents: 18712, deaths: 467, deathsPer100: 2.4957, fatal: 418, share: 45.4937, matrixLabel: "Столкновение", labelSide: "left" },
  { label: "Наезд на пешехода", accidents: 13637, deaths: 746, deathsPer100: 5.4704, fatal: 732, share: 33.155, matrixLabel: "Наезд на пешехода", labelSide: "left" },
  { label: "Наезд на велосипедиста", accidents: 2120, deaths: 32, deathsPer100: 1.5094, fatal: 31, share: 5.1543, matrixLabel: "Наезд на велосипедиста", labelSide: "left" },
  { label: "Наезд на препятствие", accidents: 2114, deaths: 209, deathsPer100: 9.8865, fatal: 178, share: 5.1397, matrixLabel: "Наезд на препятствие", labelSide: "left" },
  { label: "Падение пассажира", accidents: 2021, deaths: 14, deathsPer100: 0.6927, fatal: 14, share: 4.9136, matrixLabel: "Падение пассажира", labelSide: "left" },
  { label: "Наезд на стоящее ТС", accidents: 1573, deaths: 180, deathsPer100: 11.4431, fatal: 157, share: 3.8244, matrixLabel: "Наезд на стоящее т/с", labelSide: "left" },
  { label: "Опрокидывание", accidents: 789, deaths: 29, deathsPer100: 3.6755, fatal: 29, share: 1.9183, matrixLabel: "Опрокидывание", labelSide: "left" },
  { label: "Съезд с дороги", accidents: 49, deaths: 8, deathsPer100: 16.3265, fatal: 5, share: 0.1191, matrixLabel: "Съезд с дороги", labelSide: "right" },
  { label: "Иной вид ДТП", accidents: 35, deaths: 5, deathsPer100: 14.2857, fatal: 5, share: 0.0851, matrixLabel: "Иной вид ДТП", labelSide: "right" },
  { label: "Наезд на лицо, осуществляющее проведение работ", accidents: 31, deaths: 2, deathsPer100: 6.4516, fatal: 2, share: 0.0754, matrixLabel: "Наезд на лицо, проводившее работы", labelSide: "right" },
];

// The original frequency × severity matrix also shows the next, eleventh,
// category. The bar chart above remains a strict top 10.
const MATRIX_ACCIDENT_TYPES = [
  ...ACCIDENT_TYPES,
  { label: "Отбрасывание предмета", accidents: 21, deaths: 1, deathsPer100: 4.7619, fatal: 1, share: 0.0511, matrixLabel: "Отбрасывание предмета" },
];

const DISTRICTS = [
  { label: "ЮАО", accidents: 5303, deathsPer100: 3.5263 }, { label: "ЗАО", accidents: 4446, deathsPer100: 3.8911 },
  { label: "СВАО", accidents: 4358, deathsPer100: 3.8779 }, { label: "ЮВАО", accidents: 4324, deathsPer100: 3.8622 },
  { label: "ВАО", accidents: 4280, deathsPer100: 4.7664 }, { label: "САО", accidents: 4164, deathsPer100: 3.2661 },
  { label: "ЮЗАО", accidents: 3764, deathsPer100: 3.6132 }, { label: "ЦАО", accidents: 3675, deathsPer100: 3.0748 },
  { label: "СЗАО", accidents: 3225, deathsPer100: 2.6667 }, { label: "ТиНАО", accidents: 2270, deathsPer100: 11.1454 },
  { label: "ЗелАО", accidents: 677, deathsPer100: 3.8405 }, { label: "Московская область", accidents: 637, deathsPer100: 6.9074 },
  { label: "Не определено", accidents: 8, deathsPer100: 0 },
];

const ROAD_ELEMENTS = [
  { label: "Перегон (нет объектов на месте ДТП)", accidents: 14234 },
  { label: "Регулируемый перекресток", accidents: 5781 },
  { label: "Нерегулируемый пешеходный переход", accidents: 5311 },
  { label: "Выезд с прилегающей территории", accidents: 4098 },
  { label: "Нерегулируемый перекрёсток неравнозначных улиц (дорог)", accidents: 3510 },
  { label: "Регулируемый пешеходный переход", accidents: 3177 },
  { label: "Внутридворовая территория", accidents: 3167 },
  { label: "Тротуар, пешеходная дорожка", accidents: 1494 },
  { label: "Остановка общественного транспорта", accidents: 1490 },
  { label: "Мост, эстакада, путепровод", accidents: 1271 },
  { label: "Нерегулируемый перекрёсток равнозначных улиц (дорог)", accidents: 454 },
  { label: "Автостоянка (отделенная от проезжей части)", accidents: 317 },
];

const TIMES_OF_DAY = [
  { label: "День", accidents: 14701, share: 35.7419 }, { label: "Вечер", accidents: 13490, share: 32.7976 },
  { label: "Утро", accidents: 9522, share: 23.1504 }, { label: "Ночь", accidents: 3418, share: 8.31 },
];

const WEEKDAYS = [
  { label: "Понедельник", accidents: 5929, deathsPer100: 4.031 }, { label: "Вторник", accidents: 6090, deathsPer100: 3.7603 },
  { label: "Среда", accidents: 6138, deathsPer100: 3.682 }, { label: "Четверг", accidents: 6133, deathsPer100: 3.473 },
  { label: "Пятница", accidents: 6350, deathsPer100: 4.3622 }, { label: "Суббота", accidents: 5480, deathsPer100: 4.3431 },
  { label: "Воскресенье", accidents: 5011, deathsPer100: 5.4281 },
];

const HOURS = [
  { hour: 0, accidents: 987, deathsPer100: 11.2462 }, { hour: 1, accidents: 733, deathsPer100: 11.3233 },
  { hour: 2, accidents: 517, deathsPer100: 8.3172 }, { hour: 3, accidents: 423, deathsPer100: 10.6383 },
  { hour: 4, accidents: 346, deathsPer100: 11.2717 }, { hour: 5, accidents: 412, deathsPer100: 13.5922 },
  { hour: 6, accidents: 590, deathsPer100: 7.9661 }, { hour: 7, accidents: 1319, deathsPer100: 3.4117 },
  { hour: 8, accidents: 2038, deathsPer100: 2.3553 }, { hour: 9, accidents: 1816, deathsPer100: 2.5881 },
  { hour: 10, accidents: 1823, deathsPer100: 2.5782 }, { hour: 11, accidents: 1936, deathsPer100: 3.874 },
  { hour: 12, accidents: 2084, deathsPer100: 3.4069 }, { hour: 13, accidents: 2328, deathsPer100: 2.7491 },
  { hour: 14, accidents: 2411, deathsPer100: 2.696 }, { hour: 15, accidents: 2361, deathsPer100: 2.5837 },
  { hour: 16, accidents: 2564, deathsPer100: 2.7691 }, { hour: 17, accidents: 2953, deathsPer100: 3.3187 },
  { hour: 18, accidents: 3072, deathsPer100: 2.7995 }, { hour: 19, accidents: 2928, deathsPer100: 3.9617 },
  { hour: 20, accidents: 2388, deathsPer100: 3.9363 }, { hour: 21, accidents: 2117, deathsPer100: 4.8654 },
  { hour: 22, accidents: 1662, deathsPer100: 5.8363 }, { hour: 23, accidents: 1323, deathsPer100: 6.198 },
];

const MOTION_INFLUENCE_SEVERITY = [
  { label: "Наличие на проезжей части неисправного ТС, мешающего движению", severityIndex: 87 },
  { label: "Проведение дорожных и прочих работ без сужения проезжей части", severityIndex: 70.1 },
  { label: "Режим движения нарушен вследствие ранее произошедшего ДТП", severityIndex: 63.96 },
  { label: "Сужение проезжей части вследствие проведения работ", severityIndex: 43.8 },
  { label: "Участок, контролируемый камерами автоматической фотовидеофиксации", severityIndex: 36.23 },
  { label: "Проведение работ на тротуарах, затрудняющих движение пешеходов", severityIndex: 32.21 },
  { label: "Участок, оборудованный шумовыми полосами", severityIndex: 29.41 },
  { label: "Участок, контролируемый камерами без предупреждающего знака", severityIndex: 28.69 },
  { label: "Торговый или иной объект, затрудняющий движение", severityIndex: 26.32 },
  { label: "Конструктивное сужение проезжей части", severityIndex: 24.69 },
];

const ROAD_DRAWBACK_SEVERITY = [
  { label: "Неисправное освещение", severityIndex: 96.82 },
  { label: "Неровное покрытие", severityIndex: 79.17 },
  { label: "Отсутствие временных ТСОД в местах производства работ", severityIndex: 51.6 },
  { label: "Неисправность светофора", severityIndex: 50.96 },
  { label: "Отсутствие пешеходных ограждений в необходимых местах", severityIndex: 45.58 },
  { label: "Иные недостатки", severityIndex: 38.89 },
  { label: "Дефекты покрытия", severityIndex: 33.84 },
  { label: "Сужение проезжей части, наличие препятствий", severityIndex: 33.33 },
  { label: "Отсутствие, плохая различимость горизонтальной разметки", severityIndex: 32.03 },
  { label: "Недостатки зимнего содержания", severityIndex: 31.88 },
];

const ROAD_ELEMENT_SEVERITY = [
  { label: "АЗС", severityIndex: 78.89 },
  { label: "Регулируемый пешеходный переход у школы", severityIndex: 62.74 },
  { label: "Иное место", severityIndex: 59.64 },
  { label: "Перегон (нет объектов на месте ДТП)", severityIndex: 53.82 },
  { label: "Подход к мосту, эстакаде, путепроводу", severityIndex: 52.92 },
  { label: "Тоннель", severityIndex: 51.99 },
  { label: "Мост, эстакада, путепровод", severityIndex: 49.2 },
  { label: "Пешеходная зона", severityIndex: 45.87 },
  { label: "Нерегулируемый перекрёсток равнозначных улиц", severityIndex: 42.6 },
  { label: "Регулируемый пешеходный переход", severityIndex: 41.55 },
];

const formatNumber = new Intl.NumberFormat("ru-RU");

function ReportCard({ title, children, className = "" }) {
  return (
    <section className={`statistics-report-card ${className}`.trim()}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((item) => (
        <div key={item.dataKey}>
          <span style={{ background: item.color }} />
          {item.name}: {formatNumber.format(item.value ?? 0)}
        </div>
      ))}
    </div>
  );
}

function HorizontalBarChart({ data, height = 360, showFatal = false, axisWidth, valueKey = "accidents", valueName = "ДТП" }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 42, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 12 }} />
        <YAxis dataKey="label" type="category" width={axisWidth || (showFatal ? 198 : 220)} tick={{ fontSize: 10 }} />
        <Tooltip content={<ChartTooltip />} />
        {showFatal ? <Legend verticalAlign="top" height={30} /> : null}
        <Bar dataKey={valueKey} name={valueName} fill={showFatal ? "#0f766e" : "#636efa"} radius={[0, 4, 4, 0]}>
          {!showFatal ? <LabelList dataKey={valueKey} position="right" fill="#334155" fontSize={10} formatter={(value) => Number.isInteger(value) ? formatNumber.format(value) : value} /> : null}
        </Bar>
        {showFatal ? <Bar dataKey="fatal" name="ДТП с погибшими" fill="#b91c1c" radius={[0, 4, 4, 0]} /> : null}
      </BarChart>
    </ResponsiveContainer>
  );
}

function FrequencySeverityChart({ data, height, xDataKey = "label", xTick, xInterval, barName = "ДТП", accidentsPresentation = "bar" }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 16, right: 22, bottom: 18, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
        <XAxis dataKey={xDataKey} interval={xInterval} tick={xTick || { fontSize: 10 }} />
        <YAxis yAxisId="accidents" tick={{ fontSize: 10 }} label={{ value: "ДТП", angle: -90, position: "insideLeft", offset: -2, fontSize: 11 }} />
        <YAxis yAxisId="severity" orientation="right" tick={{ fontSize: 10 }} label={{ value: "Погибшие на 100 ДТП", angle: 90, position: "insideRight", offset: 0, fontSize: 11 }} />
        <Tooltip content={<ChartTooltip />} />
        <Legend verticalAlign="top" height={26} wrapperStyle={{ fontSize: 10 }} />
        {accidentsPresentation === "line" ? (
          <Line yAxisId="accidents" type="linear" dataKey="accidents" name={barName} stroke="#636efa" strokeWidth={2} dot={{ r: 2.5 }} />
        ) : (
          <Bar yAxisId="accidents" dataKey="accidents" name={barName} fill="#636efa" />
        )}
        <Line yAxisId="severity" type="linear" dataKey="deathsPer100" name="Погибшие на 100 ДТП" stroke="#EF553B" strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const TIME_OF_DAY_COLORS = ["#636efa", "#EF553B", "#00cc96", "#ab63fa"];

function TimeOfDayPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, payload }) {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.54;
  const angle = (-midAngle * Math.PI) / 180;
  const x = cx + radius * Math.cos(angle);
  const y = cy + radius * Math.sin(angle);
  return (
    <text x={x} y={y - 5} fill="#fff" textAnchor="middle" fontSize="10">
      <tspan x={x}>{payload.label}</tspan>
      <tspan x={x} dy="12">{payload.share.toFixed(1)}%</tspan>
    </text>
  );
}

function TimeOfDayDonut() {
  return (
    <ResponsiveContainer width="100%" height={360}>
      <PieChart>
        <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: 10 }} />
        <Tooltip content={<ChartTooltip />} />
        <Pie
          data={TIMES_OF_DAY}
          dataKey="accidents"
          nameKey="label"
          name="ДТП"
          cx="50%"
          cy="53%"
          innerRadius="41%"
          outerRadius="72%"
          labelLine={false}
          label={<TimeOfDayPieLabel />}
        >
          {TIMES_OF_DAY.map((row, index) => <Cell key={row.label} fill={TIME_OF_DAY_COLORS[index]} />)}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

function MatrixTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{row.label}</strong>
      <div>Количество ДТП: {formatNumber.format(row.accidents)}</div>
      <div>Погибшие: {formatNumber.format(row.deaths)}</div>
      <div>Погибшие на 100 ДТП: {row.deathsPer100}</div>
    </div>
  );
}

function FrequencySeverityPoint({ cx, cy, payload }) {
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !payload) return null;
  // Plotly source: marker.size = accidents, sizemode = area, sizeref = 46.78.
  // This preserves the original area ratio between the bubbles.
  const radius = Math.sqrt(payload.accidents / 46.78) / 2;
  return (
    <g>
      <circle cx={cx} cy={cy} r={radius} fill="#636efa" fillOpacity="0.78" />
      <text
        x={cx}
        y={cy - radius - 4}
        textAnchor="middle"
        fill="#334155"
        fontSize="10"
      >
        {payload.matrixLabel}
      </text>
    </g>
  );
}

function FrequencySeverityMatrix() {
  return (
    <ResponsiveContainer width="100%" height={390}>
      <ScatterChart margin={{ top: 14, right: 26, bottom: 28, left: 22 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="accidents"
          name="Количество ДТП"
          scale="log"
          domain={[1, 30000]}
          ticks={[1, 2, 5, 10, 100, 200, 500, 1000, 2000, 5000, 10000, 20000]}
          tick={{ fontSize: 10 }}
          label={{ value: "Количество ДТП, логарифмическая шкала", position: "bottom", offset: 10, fontSize: 11 }}
        />
        <YAxis
          type="number"
          dataKey="deathsPer100"
          name="Погибшие на 100 ДТП"
          domain={[0, 17]}
          ticks={[0, 2, 4, 6, 8, 10, 12, 14, 16]}
          tick={{ fontSize: 10 }}
          label={{ value: "Погибшие на 100 ДТП", angle: -90, position: "insideLeft", offset: -8, fontSize: 11 }}
        />
        <Tooltip content={<MatrixTooltip />} cursor={{ strokeDasharray: "3 3" }} />
        <Scatter data={MATRIX_ACCIDENT_TYPES} shape={<FrequencySeverityPoint />} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export default function DescriptiveReportSection() {
  return (
    <div className="statistics-descriptive-report">
      <section className="statistics-descriptive-hero">
        <h2>Описательная статистика: где, когда и какие ДТП происходят</h2>
        <p>
          Вкладка объединяет наиболее содержательные срезы без необходимости переключать десятки фильтров.
          Частота показывает масштаб проблемы, а показатели тяжести помогают отделить массовые категории от
          редких, но особенно опасных.
        </p>
      </section>

      <div className="statistics-report-kpi-grid statistics-descriptive-kpi-grid">
        <article className="statistics-report-kpi"><span>Самый частый вид</span><strong className="statistics-report-kpi-text-value">Столкновение</strong><small>18 712 ДТП, 45,5% совокупности</small></article>
        <article className="statistics-report-kpi"><span>Высокочастотный тяжёлый вид</span><strong className="statistics-report-kpi-text-value">Наезд на пешехода</strong><small>13 637 ДТП, 5,47 погибшего на 100 ДТП</small></article>
        <article className="statistics-report-kpi"><span>Лидер по числу ДТП</span><strong>ЮАО</strong><small>5 303 ДТП</small></article>
        <article className="statistics-report-kpi"><span>Лидер по относительной тяжести</span><strong>ТиНАО</strong><small>Наибольшая относительная тяжесть среди округов</small></article>
        <article className="statistics-report-kpi"><span>Самая массовая зона УДС</span><strong className="statistics-report-kpi-text-value">Перегон</strong><small>14 234 ДТП</small></article>
      </div>

      <div className="statistics-report-section-heading">
        <h3>Виды ДТП и тяжесть последствий</h3>
        <p>Первые десять видов формируют практически весь массив происшествий.</p>
      </div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Наиболее частые виды ДТП" className="statistics-report-chart-card">
          <HorizontalBarChart data={ACCIDENT_TYPES} height={390} />
        </ReportCard>
        <ReportCard title="Матрица «частота × тяжесть» по видам ДТП" className="statistics-report-chart-card">
          <FrequencySeverityMatrix />
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading">
        <h3>Территория и элементы УДС</h3>
      </div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Округа: число ДТП и относительная тяжесть" className="statistics-report-chart-card">
          <FrequencySeverityChart data={DISTRICTS} height={430} xInterval={0} xTick={{ fontSize: 9, angle: -42, textAnchor: "end" }} />
        </ReportCard>
        <ReportCard title="Наиболее массовые элементы УДС" className="statistics-report-chart-card">
          <HorizontalBarChart data={ROAD_ELEMENTS} height={430} axisWidth={236} />
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Временная структура</h3></div>
      <div className="statistics-report-chart-grid statistics-report-chart-grid-three">
        <ReportCard title="Распределение по времени суток" className="statistics-report-chart-card">
          <TimeOfDayDonut />
        </ReportCard>
        <ReportCard title="Дни недели: частота и тяжесть" className="statistics-report-chart-card">
          <FrequencySeverityChart data={WEEKDAYS} height={360} xInterval={0} xTick={{ fontSize: 9, angle: -42, textAnchor: "end" }} />
        </ReportCard>
        <ReportCard title="Почасовой профиль аварийности" className="statistics-report-chart-card">
          <FrequencySeverityChart data={HOURS} height={360} xDataKey="hour" xInterval={1} xTick={{ fontSize: 9 }} accidentsPresentation="line" />
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading">
        <h3>Индексы тяжести</h3>
        <p>Комплексный рейтинг объединяет смертность, пострадавших и детскую аварийность.</p>
      </div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Топ-10 дорожных и организационных факторов по индексу тяжести" className="statistics-report-chart-card">
          <HorizontalBarChart data={MOTION_INFLUENCE_SEVERITY} valueKey="severityIndex" valueName="Индекс тяжести" axisWidth={290} height={410} />
        </ReportCard>
        <ReportCard title="Топ-10 недостатков дороги и ТСОДД по индексу тяжести" className="statistics-report-chart-card">
          <HorizontalBarChart data={ROAD_DRAWBACK_SEVERITY} valueKey="severityIndex" valueName="Индекс тяжести" axisWidth={250} height={410} />
        </ReportCard>
      </div>
      <ReportCard title="Элементы УДС с наиболее высоким индексом тяжести" className="statistics-report-chart-card statistics-report-wide-card">
        <HorizontalBarChart data={ROAD_ELEMENT_SEVERITY} valueKey="severityIndex" valueName="Индекс тяжести" axisWidth={275} height={430} />
      </ReportCard>

      <div className="statistics-report-section-heading"><h3>Интерпретация</h3></div>
      <div className="statistics-report-interpretation-grid">
        <article className="statistics-report-card statistics-report-findings-card">Днём происходит больше всего ДТП, однако ночные происшествия заметно тяжелее: около <strong>11 погибших на 100 ДТП</strong>.</article>
        <article className="statistics-report-card statistics-report-findings-card">Воскресенье характеризуется меньшим числом ДТП, но более высокой относительной тяжестью, чем большинство рабочих дней.</article>
        <article className="statistics-report-card statistics-report-findings-card">Среди недостатков дороги максимальный индекс тяжести получило неисправное освещение, а среди организационных факторов — наличие неисправного ТС, мешающего движению.</article>
      </div>
    </div>
  );
}
